import { memo, useCallback, useMemo } from 'react';

import { CircleAlertIcon, CircleCheckIcon, CircleHelpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';

import {
  getPerformanceKey,
  MAX_THRESHOLD_PERCENTAGE,
  MIN_THRESHOLD_PERCENTAGE,
  Performance,
} from './utils';

import styles from './PerformanceBar.module.scss';

interface Props {
  phrases: number;
  optimalPhrases: number;
}

export const PerformanceBar = memo(({ phrases, optimalPhrases }: Props) => {
  const { t } = useTranslation();

  const percentage = useMemo(() => (phrases / optimalPhrases) * 100, [optimalPhrases, phrases]);

  const getBarText = useCallback(
    (percentage: number, optimalPhrases: number): string => {
      const performance = getPerformanceKey(percentage);
      return performance ? t(`intent.performance_bar.${performance}`, { 0: optimalPhrases }) : '';
    },
    [t]
  );

  return (
    <div className={styles.wrapper}>
      <div>
        <span className={cn('text-sm leading-5 font-semibold text-muted-foreground', styles.title)}>
          {t('intent.performance_bar.title')}
        </span>
        <InfoTooltip
          content={t('intent.performance_bar.tooltip', { 0: optimalPhrases })}
          side="right"
          size="large"
        >
          {/* The span restores the gutter and gives Radix a real element to anchor to. */}
          <span className="ml-1 inline-flex">
            <CircleHelpIcon size={12} className="text-muted-foreground" />
          </span>
        </InfoTooltip>
      </div>
      <div className={styles.barWrapper}>
        <div className={styles.primaryBar}>
          <div
            className={cn(styles.inner, styles[getPerformanceKey(percentage)])}
            style={{ width: `${percentage >= MIN_THRESHOLD_PERCENTAGE ? 100 : percentage * 2}%` }}
          >
            {percentage <= MAX_THRESHOLD_PERCENTAGE && (
              <span className={styles.tick}>
                <CircleCheckIcon
                  size={14}
                  color={
                    percentage >= MIN_THRESHOLD_PERCENTAGE
                      ? 'var(--muted-foreground)'
                      : 'var(--border)'
                  }
                />
              </span>
            )}
          </div>
        </div>

        {getPerformanceKey(percentage) === Performance.TOO_MANY && (
          <div className={styles.secondary}>
            <span className={styles.warning}>
              <CircleAlertIcon className="text-warning" size={14} />
            </span>
          </div>
        )}
      </div>

      <span className="text-sm leading-5 font-normal text-muted-foreground">
        {getBarText(percentage, optimalPhrases)}.
      </span>
    </div>
  );
});

PerformanceBar.displayName = 'PerformanceBar';
